/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AddTestCampaignDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Stack } from "@mui/material";
import { useDispatch } from "react-redux";
import { adminSettingsAPI, HandleApiActions } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";

function AddTestCampaignDialog({
  open,
  onClose,
  isRecreateDialog = false,
  onSuccess,
}) {
  // Dispatch
  const dispatch = useDispatch();
  // campaign name
  const [campaignName, setCampaignName] = useState("");
  const [campaignNameError, setCampaignNameError] = useState("");
  // Project
  const [projectsOptionsList, setProjectsOptionsList] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [enableProjectLoading, setEnableProjectLoading] = useState(false);
  // Variant
  const [variantsOptionsList, setVariantsOptionsList] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(null);

  const [milestoneOptionsList, setMilestoneOptionsList] = useState([]);
  const [milestone, setMilestone] = useState([]);

  const [enableVariantLoading, setEnableVariantLoading] = useState(false);

  // recreate state
  const [parentProjectId, setParentProjectId] = useState(null);
  const [parentVariantID, setParentVariantID] = useState(null);
  const [projectList, setProjectList] = useState([]);
  const [variantList, setVariantList] = useState([]);
  const [parentTestCampaignList, setParentTestCampaignList] = useState([]);
  const [selectedTestCampaign, setSelectedTestCampaign] = useState(null);
  const [enableCampaignLoading, setEnableCampaignLoading] = useState(false);
  // is update test campaign valid
  const isUpdateTestCampaignValid = () => {
    const hasProject = selectedProjectId !== null && selectedProjectId !== "";
    const hasVariant = selectedVariantId !== null && selectedVariantId !== "";

    return (
      campaignName?.trim() !== "" &&
      !campaignNameError &&
      hasProject &&
      hasVariant &&
      (!isRecreateDialog || selectedTestCampaign?.campaign_id)
    );
  };

  // Clear add test campaign fields
  const clearAddTestCampaignFields = () => {
    // campaign name
    setCampaignName("");
    setCampaignNameError("");
    // Project
    setProjectsOptionsList([]);
    setSelectedProjectId(null);
    // Variant
    setVariantsOptionsList([]);
    setSelectedVariantId(null);
    setParentProjectId(null);
    setParentVariantID(null);
    setSelectedTestCampaign(null);
  };

  // Handle update test campaign
  const handleAddTestCampaign = async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.add_test_campaign,
        params: {
          name: campaignName,
          project_id: selectedProjectId,
          variant_id: selectedVariantId,
          milestone_id: milestone?.map((ms) => ms.value) || [],
        },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      if (onSuccess) {
        onSuccess();
      }
      onClose(false);
      clearAddTestCampaignFields();
    }
  };

  // recreate functions
  const getParentCampaignOnVariantSelect = async (selectedPArentVariantID) => {
    setEnableCampaignLoading(true);
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.get_Parent_test_campaign,
        params: {
          project_id: selectedProjectId,
          parent_project_id: parentProjectId,
          parent_variant_id: selectedPArentVariantID,
        },
      }),
    );
    setEnableCampaignLoading(false);
    const parentCampaignResponse = resp?.data?.length ? resp?.data : [];
    const filteredParentTestCampaignList = parentCampaignResponse.map((tc) => ({
      value: tc,
      label: tc.name,
    }));
    setParentTestCampaignList(filteredParentTestCampaignList);
  };

  const handleRecreateTestCampaign = async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.recreate_test_campaign,
        params: {
          name: campaignName,
          project_id: selectedProjectId,
          variant_id: selectedVariantId,
          parent_project_id: parentProjectId,
          parent_variant_id: parentVariantID,
          parent_campaign_id: selectedTestCampaign?.campaign_id,
        },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      if (onSuccess) {
        onSuccess();
      }
      onClose(false);
      clearAddTestCampaignFields();
    }
  };

  const setFormData = (campaign) => {
    setCampaignName(campaign.name);
  };

  const getMilestones = async ({ projectId, variantId }) => {
    const resp = await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_vehicle_status,
        params: {
          page_number: 1,
          page_size: 1000,
          project_id: projectId,
          variant_id: variantId,
        },
      }),
    );
    if (!resp.error) {
      const filteredMilestoneOptionsList = resp?.data?.map((ms) => ({
        value: ms?.id,
        label: ms?.name,
      }));
      setMilestoneOptionsList(filteredMilestoneOptionsList);
    }
  };

  // Get Projects
  useEffect(() => {
    const handleGetProjects = async () => {
      setEnableProjectLoading(true);
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_projects,
          params: {
            sort_column: "created_at",
            sort_order: "DESC",
            ...(isRecreateDialog && { recreated_project: true }),
            page_number: 1,
            page_size: 10000,
          },
        }),
      );
      setEnableProjectLoading(false);
      if (resp.error) {
        setProjectsOptionsList([]);
        setSelectedProjectId(null);
      } else {
        const filteredProjectsOptionsList = resp?.data?.map((pr) => ({
          value: pr?.project_id,
          label: pr?.name,
        }));
        setProjectList(resp?.data);
        setProjectsOptionsList(filteredProjectsOptionsList);
        setSelectedProjectId(null);
      }
    };

    if (open) {
      handleGetProjects();
    }
  }, [dispatch, isRecreateDialog, open]);

  // Get Variants
  useEffect(() => {
    const handleGetVariants = async ({ projectId }) => {
      setEnableVariantLoading(true);
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_variants,
          params: {
            project_id: projectId,
            is_complete_details_required: false,
            ...(isRecreateDialog && { recreated_variant: true }),
            sort_column: "name",
            sort_order: "ASC",
            page_number: 1,
            page_size: 500,
          },
        }),
      );
      setEnableVariantLoading(false);
      if (resp.error) {
        setVariantsOptionsList([]);
        setSelectedVariantId(null);
      } else {
        const filteredVariantsOptionsList = resp?.data?.map((vr) => ({
          value: vr?.variant_id,
          label: vr?.name,
        }));
        setVariantList(resp?.data);
        setVariantsOptionsList(filteredVariantsOptionsList);
        setSelectedVariantId(null);
      }
    };

    if (selectedProjectId !== null && selectedProjectId !== "") {
      handleGetVariants({ projectId: selectedProjectId });
    }
  }, [dispatch, isRecreateDialog, selectedProjectId]);

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={() => {
        onClose(false);
        clearAddTestCampaignFields();
      }}
      title={isRecreateDialog ? "Recreate Campaign" : "Add Campaign"}
      successButtonName={"Add"}
      rejectButtonName={"Close"}
      onClickSuccess={() => {
        if (isRecreateDialog) {
          handleRecreateTestCampaign();
        } else {
          handleAddTestCampaign();
        }
      }}
      onDisabled={!isUpdateTestCampaignValid()}
      actionButtonStyle={"continuous"}
      onClickReject={() => {
        onClose(false);
        clearAddTestCampaignFields();
      }}
    >
      <Stack
        spacing={2}
        mb={2}
        sx={{
          minWidth: "290px",
          maxWidth: "290px",
          ...(isRecreateDialog
            ? { minHeight: "300px" }
            : { minHeight: "280px" }),
        }}
      >
        {!isRecreateDialog && (
          <CustomFormTextFieldV2
            label="Campaign Name"
            value={campaignName}
            setValue={setCampaignName}
            required={true}
          />
        )}
        <CustomSelectV2
          size="small"
          label="Project"
          required={true}
          enableLoading={enableProjectLoading}
          optionsList={projectsOptionsList}
          enableSearch={true}
          disablePortal={false}
          selectedValue={selectedProjectId ?? ""}
          handleOnChange={(e) => {
            const selectedValue = e.target.value;
            setSelectedProjectId(selectedValue);
            setSelectedVariantId(null);
            setVariantsOptionsList([]);
            setSelectedTestCampaign(null);
            setMilestone([]);
            setMilestoneOptionsList([]);
            const selectedProject = projectList.find(
              (p) => String(p.project_id) === String(selectedValue),
            );
            setParentProjectId(selectedProject?.parent_project_id ?? null);
          }}
          sltFldStyle={{ width: "270px" }}
        />
        <CustomSelectV2
          size="small"
          label="Variant"
          required={true}
          optionsList={variantsOptionsList}
          enableLoading={enableVariantLoading}
          disablePortal={false}
          selectedValue={selectedVariantId ?? ""}
          handleOnChange={(e) => {
            const selectedValue = e.target.value;
            setSelectedVariantId(selectedValue);
            setMilestone([]);
            getMilestones({
              projectId: selectedProjectId,
              variantId: selectedValue,
            });
            const selectedVariant = variantList.find(
              (v) => String(v.variant_id) === String(selectedValue),
            );
            const parentVariantId =
              selectedVariant?.parent_variant_details?.variant_id ?? null;
            setParentVariantID(parentVariantId);
            if (isRecreateDialog && parentVariantId) {
              setSelectedTestCampaign(null);
              setCampaignName("");
              getParentCampaignOnVariantSelect(parentVariantId);
            }
          }}
          sltFldStyle={{ width: "270px" }}
        />

        <CustomMultiSelectV2
          label="Milestone"
          required={true}
          optionsList={milestoneOptionsList}
          selectedValues={milestone}
          setSelectedValues={setMilestone}
        />

        {isRecreateDialog && (
          <>
            <CustomSelectV2
              size="small"
              label="Parent Campaign"
              required={true}
              enableLoading={enableCampaignLoading}
              optionsList={parentTestCampaignList}
              disablePortal={false}
              selectedValue={selectedTestCampaign || null}
              handleOnChange={(e) => {
                setSelectedTestCampaign(e.target.value);
                setFormData(e.target.value);
              }}
              sltFldStyle={{ width: "270px" }}
            />

            <CustomFormTextFieldV2
              label="Campaign Name"
              value={campaignName}
              setValue={setCampaignName}
              required={true}
            />
          </>
        )}
      </Stack>
    </CustomDialogBoxV2>
  );
}

AddTestCampaignDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isRecreateDialog: PropTypes.bool,
  onSuccess: PropTypes.func,
};

export default AddTestCampaignDialog;
